import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import dateUtils from '../../utils/dateUtils';
import './CommonStyles.css';
import './AddSellStyles.css';

const today = () => dateUtils.getChicagoISOString().split('T')[0];

const normalizeOptions = (value) => {
  if (Array.isArray(value)) return value.map((item) => (typeof item === 'object' ? item : { id: item, name: item }));
  if (value && typeof value === 'object') {
    return Object.entries(value).map(([id, name]) => ({ id, name }));
  }
  return [];
};

const AddPurchaseOrder = ({ onBack }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    contact_id: '',
    location_id: '',
    ref_no: '',
    transaction_date: today(),
    delivery_date: '',
    shipping_status: '',
    tax_id: '',
    tax_amount: 0,
    discount_type: 'Percentage',
    discount_amount: '',
    shipping_charges: '',
    additional_notes: '',
    products: [],
  });

  const [suppliers, setSuppliers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [shippingStatuses, setShippingStatuses] = useState({});
  const [productOptions, setProductOptions] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await apiService.getPurchaseOrderCreateData();
        if (result.success) {
          const data = result.data || {};
          setLocations(normalizeOptions(data.business_locations));
          setTaxes(data.taxes || []);
          setShippingStatuses(data.shipping_statuses || {});
        }
      } catch (err) {
      /* console.error('Error fetching purchase order create data:', err); */
      }
      try {
        const res = await apiService.getContacts({ type: 'supplier', per_page: 100 });
        const list = Array.isArray(res) ? res : (res.data || []);
        setSuppliers(list);
      } catch (err) {
      /* console.error('Error fetching suppliers:', err); */
      }
    };
    fetchData();
  }, []);

  const searchProducts = async (term) => {
    if (!term.trim()) {
      setProductOptions([]);
      return;
    }
    setIsLoadingProducts(true);
    try {
      const res = await apiService.getProducts({ search: term, per_page: 50 });
      const list = Array.isArray(res) ? res : (res.data || []);
      setProductOptions(list);
    } catch (err) {
      /* console.error('Error searching products:', err); */
      setProductOptions([]);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => searchProducts(productSearchTerm), 400);
    return () => clearTimeout(timer);
  }, [productSearchTerm]);

  const handleChange = (key, value) => {
    setForm((prev) => {
      const updated = { ...prev, [key]: value };
      if (key === 'tax_id') {
        const tax = taxes.find((t) => String(t.id) === String(value));
        updated.tax_amount = tax ? (subtotal * (parseFloat(tax.amount) || 0) / 100) : 0;
      }
      return updated;
    });
  };

  const addProduct = (product) => {
    const exists = form.products.find((p) => String(p.product_id) === String(product.id));
    if (exists) return;
    const unitCost = parseFloat(product.purchase_price_inc_tax) || parseFloat(product.default_purchase_price) || 0;
    setForm((prev) => ({
      ...prev,
      products: [
        ...prev.products,
        {
          id: Date.now(),
          product_id: product.id,
          variation_id: product.product_variations?.[0]?.variations?.[0]?.id || product.variation_id || product.id,
          name: product.name,
          sku: product.sku || '',
          quantity: 1,
          unit_cost: unitCost,
          tax_percent: 0,
        },
      ],
    }));
    setProductSearchTerm('');
  };

  const updateProduct = (id, key, value) => {
    setForm((prev) => ({
      ...prev,
      products: prev.products.map((p) => (p.id !== id ? p : { ...p, [key]: value })),
    }));
  };

  const removeProduct = (id) => setForm((prev) => ({ ...prev, products: prev.products.filter((p) => p.id !== id) }));

  const subtotal = form.products.reduce((s, p) => s + (parseFloat(p.quantity) || 0) * (parseFloat(p.unit_cost) || 0), 0);
  const discountVal = parseFloat(form.discount_amount) || 0;
  const discountAmt = form.discount_type === 'Percentage' ? subtotal * discountVal / 100 : discountVal;
  const selectedTax = taxes.find((t) => String(t.id) === String(form.tax_id));
  const taxRate = selectedTax ? parseFloat(selectedTax.amount) || 0 : 0;
  const taxAmt = (subtotal - discountAmt) * taxRate / 100;
  const shipping = parseFloat(form.shipping_charges) || 0;
  const finalTotal = Math.max(0, subtotal - discountAmt + taxAmt + shipping);

  const validate = () => {
    const newErrors = {};
    if (!form.contact_id) newErrors.contact_id = 'Supplier is required';
    if (!form.location_id) newErrors.location_id = 'Business location is required';
    if (!form.transaction_date) newErrors.transaction_date = 'Date is required';
    if (form.products.length === 0) newErrors.products = 'Please add at least one product';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = () => {
    const transaction_data = {
      ...form,
      total_before_tax: subtotal.toFixed(2),
      tax_amount: taxAmt.toFixed(2),
      final_total: finalTotal.toFixed(2),
      purchases: form.products.map((p) => ({
        product_id: p.product_id,
        variation_id: p.variation_id,
        quantity: p.quantity,
        purchase_price: p.unit_cost,
        purchase_price_inc_tax: p.unit_cost,
        pp_without_discount: p.unit_cost,
        item_tax: 0,
      })),
    };
    return transaction_data;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      const result = await apiService.createPurchaseOrder(buildPayload());
      if (result.success) {
        window.alert('Purchase order saved successfully.');
        if (onBack) onBack();
        else navigate('/purchase_order');
      } else {
        window.alert(result.message || 'Failed to save purchase order');
      }
    } catch (err) {
      window.alert(err.message || 'Failed to save purchase order');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="sell-form-container add-sell-override">
      <div className="page-header">
        <button
          type="button"
          onClick={onBack || (() => navigate('/purchase_order'))}
          style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '1.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '40px', height: '40px' }}
          title="Back"
        >←</button>
        <h2 style={{ fontSize: '1.25rem' }}>Add Purchase Order</h2>
      </div>

      <form className="sell-form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        <div className="form-section-content">
          <div className="form-row">
            <div className="form-group">
              <label>Supplier:</label>
              <select
                className={`form-select ${errors.contact_id ? 'error' : ''}`}
                value={form.contact_id}
                onChange={(e) => handleChange('contact_id', e.target.value)}
              >
                <option value="">Please Select</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>{s.supplier_business_name || s.name}</option>
                ))}
              </select>
              {errors.contact_id && <span className="error-text">{errors.contact_id}</span>}
            </div>

            <div className="form-group">
              <label>Business Location:</label>
              <select
                className={`form-select ${errors.location_id ? 'error' : ''}`}
                value={form.location_id}
                onChange={(e) => handleChange('location_id', e.target.value)}
              >
                <option value="">Please Select</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
              {errors.location_id && <span className="error-text">{errors.location_id}</span>}
            </div>

            <div className="form-group">
              <label>Reference No:</label>
              <input type="text" className="form-input" value={form.ref_no} onChange={(e) => handleChange('ref_no', e.target.value)} />
            </div>

            <div className="form-group">
              <label>Date:</label>
              <input
                type="date"
                className={`form-input ${errors.transaction_date ? 'error' : ''}`}
                value={form.transaction_date}
                onChange={(e) => handleChange('transaction_date', e.target.value)}
              />
              {errors.transaction_date && <span className="error-text">{errors.transaction_date}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Delivery Date:</label>
              <input type="date" className="form-input" value={form.delivery_date} onChange={(e) => handleChange('delivery_date', e.target.value)} />
            </div>

            <div className="form-group">
              <label>Shipping Status:</label>
              <select className="form-select" value={form.shipping_status} onChange={(e) => handleChange('shipping_status', e.target.value)}>
                <option value="">Please Select</option>
                {Object.entries(shippingStatuses).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Purchase Tax:</label>
              <select className="form-select" value={form.tax_id} onChange={(e) => handleChange('tax_id', e.target.value)}>
                <option value="">None</option>
                {taxes.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} ({t.amount}%)</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Discount Type:</label>
              <select className="form-select" value={form.discount_type} onChange={(e) => handleChange('discount_type', e.target.value)}>
                <option value="Percentage">Percentage</option>
                <option value="Fixed">Fixed</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Discount Amount:</label>
              <input type="number" className="form-input" value={form.discount_amount} onChange={(e) => handleChange('discount_amount', e.target.value)} />
            </div>

            <div className="form-group">
              <label>Shipping Charges:</label>
              <input type="number" className="form-input" value={form.shipping_charges} onChange={(e) => handleChange('shipping_charges', e.target.value)} />
            </div>

            <div className="form-group">
              <label>Additional Notes:</label>
              <textarea className="form-input" rows={1} value={form.additional_notes} onChange={(e) => handleChange('additional_notes', e.target.value)} />
            </div>
          </div>
        </div>

        <hr className="section-divider" />

        <div className="form-section-content">
          <div className="form-row">
            <div className="form-group full-width" style={{ position: 'relative' }}>
              <label>Search Products:</label>
              <input
                type="text"
                className="form-input"
                placeholder="Search products by name / SKU"
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
              />
              {productSearchTerm && (
                <div
                  className="product-search-dropdown-menu"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: '#ffffff',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    zIndex: 9999,
                    maxHeight: '260px',
                    overflowY: 'auto',
                    marginTop: '4px',
                    padding: '0.25rem 0'
                  }}
                >
                  {isLoadingProducts ? (
                    <div style={{ padding: '0.75rem 1rem', color: '#64748b', fontSize: '0.875rem' }}>⏳ Searching products...</div>
                  ) : productOptions.length === 0 ? (
                    <div style={{ padding: '0.75rem 1rem', color: '#94a3b8', fontSize: '0.875rem', fontStyle: 'italic' }}>No products found</div>
                  ) : (
                    productOptions.map((p) => {
                      const variation = p.product_variations?.[0]?.variations?.[0] || {};
                      const skuVal = p.sku || p.sub_sku || variation.sub_sku || 'N/A';
                      const priceVal = parseFloat(
                        p.purchase_price_inc_tax ||
                        p.default_purchase_price ||
                        variation.dpp_inc_tax ||
                        variation.default_purchase_price ||
                        p.purchase_price ||
                        0
                      );
                      return (
                        <div
                          key={p.id}
                          onClick={() => addProduct(p)}
                          style={{
                            padding: '0.6rem 1rem',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: '1px solid #f1f5f9',
                            transition: 'background 0.15s ease'
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = '#ffffff')}
                        >
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#1e293b' }}>
                              {p.name || p.product}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                              SKU: <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{skuVal}</span>
                            </div>
                          </div>
                          <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#2563eb' }}>
                            ${priceVal.toFixed(2)}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

            </div>
          </div>

          {errors.products && <div className="error-text mb-2">{errors.products}</div>}

          <div className="product-table" style={{ overflowX: 'auto' }}>
            <table style={{ minWidth: '600px', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: '4%' }}>#</th>
                  <th>Product</th>
                  <th style={{ width: '18%' }}>SKU</th>
                  <th style={{ width: '18%' }}>Quantity</th>
                  <th style={{ width: '18%' }}>Unit Cost</th>
                  <th style={{ width: '18%' }}>Line Total</th>
                  <th style={{ width: '4%' }}></th>
                </tr>
              </thead>
              <tbody>
                {form.products.length === 0 ? (
                  <tr><td colSpan={7} className="no-products">No products added</td></tr>
                ) : (
                  form.products.map((p, idx) => (
                    <tr key={p.id}>
                      <td style={{ textAlign: 'center', color: '#64748b', fontSize: '0.8rem' }}>{idx + 1}</td>
                      <td style={{ fontWeight: '500', fontSize: '0.875rem' }}>{p.name}</td>
                      <td>{p.sku}</td>
                      <td>
                        <input
                          type="number"
                          className="form-input"
                          value={p.quantity}
                          min="0"
                          step="any"
                          onChange={(e) => updateProduct(p.id, 'quantity', e.target.value)}
                          style={{ width: '100%' }}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-input"
                          value={p.unit_cost}
                          min="0"
                          step="any"
                          onChange={(e) => updateProduct(p.id, 'unit_cost', e.target.value)}
                          style={{ width: '100%' }}
                        />
                      </td>
                      <td>{((parseFloat(p.quantity) || 0) * (parseFloat(p.unit_cost) || 0)).toFixed(2)}</td>
                      <td><button type="button" className="remove-product-btn" onClick={() => removeProduct(p.id)}>✕</button></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="totals-section" style={{ marginTop: '1rem', textAlign: 'right' }}>
            <div><strong>Subtotal:</strong> {subtotal.toFixed(2)}</div>
            <div><strong>Discount:</strong> {discountAmt.toFixed(2)}</div>
            <div><strong>Tax:</strong> {taxAmt.toFixed(2)}</div>
            <div><strong>Shipping:</strong> {shipping.toFixed(2)}</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700 }}><strong>Final Total:</strong> {finalTotal.toFixed(2)}</div>
          </div>
        </div>

        <div className="form-section-content action-buttons-section">
          <div className="action-buttons">
            <button type="submit" className="save-btn" disabled={isSaving}>💾 {isSaving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddPurchaseOrder;
